import classNames from "classnames";
import type React from "react";
import { useEffect, useState } from "react";
import { haveMatchingElementsById } from "../../common/utils/array.util";
import { useDebounce } from "../../utils/customHook.util";
import { performOrOperatorSearch } from "../../utils/filter.util";
import "./SearchFilter.css";
import { Flex } from "./Flex";
import Icon from "./Icon";

const minimumCharLengthToSearch = 3;

export type SearchFilterProps<T> = {
    items: T[];
    predicates: ((item: T) => string | undefined)[];
    onChange: (search: {
        isNewResult: boolean;
        matches: T[] | null;
        term: string;
    }) => void;
    onEnter?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
    className?: string;
    placeholder?: string;
};

const SearchFilter = function SearchFilter<T extends { id: string }>(
    props: SearchFilterProps<T>,
) {
    const {
        className,
        placeholder,
        items,
        predicates,
        onChange,
        onEnter,
        onFocus,
        onBlur,
    } = props;
    const [searchResult, setSearchResult] = useState<{
        matches: T[] | null;
        isNewResult: boolean;
    }>({ matches: null, isNewResult: true });
    const [searchTerm, setSearchTerm] = useState<string>("");
    const debouncedSearchTerm = useDebounce<string>(searchTerm, 200);

    const onSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setSearchTerm(newValue);
    };

    const performSearch = (onTerm: string) => {
        const matches = performOrOperatorSearch<T>(
            onTerm,
            minimumCharLengthToSearch,
            predicates,
            items,
        );

        if (onTerm.length >= minimumCharLengthToSearch) {
            setSearchResult({
                matches,
                isNewResult: !haveMatchingElementsById(matches, searchResult.matches),
            });
        } else {
            setSearchResult({
                matches: null,
                isNewResult: searchResult.matches !== null,
            });
        }
    };

    const reset = () => {
        setSearchTerm("");
        setSearchResult({
            matches: null,
            isNewResult: true,
        });
    };

    useEffect(() => {
        performSearch(debouncedSearchTerm);
    }, [debouncedSearchTerm, items, predicates]);

    useEffect(() => {
        const { matches, isNewResult } = searchResult;
        onChange({ isNewResult, matches, term: debouncedSearchTerm });
    }, [searchResult]);

    return (
        <div className={classNames("search-filter", className)}>
            <Icon.Search.Small className="search-filter__icon" />
            <input
                type="text"
                value={searchTerm}
                placeholder={placeholder}
                onChange={onSearchTermChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={(event) => {
                    switch (event.key) {
                        case "Enter":
                            if (onEnter) {
                                event.preventDefault();
                                onEnter();
                            }
                            break;
                        default:
                            break;
                    }
                }}
            />
            {searchResult.matches && (
                <Flex.Row
                    className="search-filter__matches"
                    justifyContent="end"
                    onClick={reset}
                >
                    <span>{`${searchResult.matches.length} match${
                        searchResult.matches.length === 1 ? "" : "es"
                    }`}</span>
                    <div>
                        <Flex.Row>
                            <Icon.Close.Small />
                        </Flex.Row>
                    </div>
                </Flex.Row>
            )}
        </div>
    );
};

export default SearchFilter;
